import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import PublicApp from "./PublicApp";
import "./index.css";
import { FtmDataServiceProvider } from "./context/FtmDataServiceContext";
import { createFtmDataService } from "./services/ftmDataService";
import type { FtmDataService } from "./services/ftmDataProvider";
import { createCloudFtmDataProvider } from "./services/cloudFtmDataProvider";
import { shouldRenderCloudStudio } from "./cloudStudioMode";

const contentMode = import.meta.env.VITE_FTM_CONTENT_MODE ?? (import.meta.env.DEV ? "static" : "cloud");

// Cloud Studio is an explicit remote build mode for preview or production.
// Local development keeps its existing dev entry point and public-only builds
// remain public-only unless the environment is explicitly selected.
const cloudStudioRemote = shouldRenderCloudStudio(import.meta.env.VITE_FTM_STUDIO_MODE, import.meta.env.VITE_FTM_ENVIRONMENT);

const previewMode = import.meta.env.DEV
  ? new URLSearchParams(window.location.search).get("preview") === "qa" ? "qa" : new URLSearchParams(window.location.search).get("preview") === "demo" ? "demo" : false
  : false;

async function resolveDataService(): Promise<FtmDataService> {
  if (previewMode !== "demo") {
    if (contentMode === "static") {
      const { createJsonFtmDataProvider } = await import("./services/jsonFtmDataProvider");
      return createFtmDataService(createJsonFtmDataProvider());
    }
    return createFtmDataService(createCloudFtmDataProvider());
  }
  const { createDemoFtmDataProvider } = await import("./services/demoFtmDataProvider");
  return createFtmDataService(createDemoFtmDataProvider());
}

function renderPublicApp(root: HTMLElement, service: FtmDataService): void {
  ReactDOM.createRoot(root).render(
    <React.StrictMode>
      <BrowserRouter basename={import.meta.env.BASE_URL}>
        <FtmDataServiceProvider service={service}><PublicApp previewMode={previewMode === "demo"} /></FtmDataServiceProvider>
      </BrowserRouter>
    </React.StrictMode>,
  );
}

async function boot(): Promise<void> {
  const root = document.getElementById("root")!;
  const service = await resolveDataService();
  if (cloudStudioRemote) {
    const { renderDevelopmentApp } = await import("./studio/devRuntime");
    await renderDevelopmentApp(root, service, import.meta.env.BASE_URL, previewMode);
    return;
  }
  if (import.meta.env.DEV) {
    // The smoke boundary expects import("./studio/devRuntime") to remain the DEV entry point.
    const developmentRuntime = "./studio/devRuntime";
    const { renderDevelopmentApp } = await import(/* @vite-ignore */ developmentRuntime);
    await renderDevelopmentApp(root, service, import.meta.env.BASE_URL, previewMode);
    return;
  }
  renderPublicApp(root, service);
}

void boot();
